<template>
  <div>
    <v-row v-if="domain.load_error">
      <v-col cols="12">
        <gg-card>
          <gg-card-header>
            Error
          </gg-card-header>
          <gg-card-text>
            {{ domain.load_error }}
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <gg-loading :loading="!domainPopulated" type="card">
          <gg-card>
            <gg-card-header>
              Nameserver Details
            </gg-card-header>
            <gg-card-text>
              <v-col  md="4">
                <gg-select
                  label="Select preset"
                  v-model="selected_preset"
                  :items="dns_presets"
                  item-text="printableName"
                  item-value="name"
                  outlined
                ></gg-select>
                <v-form>
                  <div v-for="(item, key) in dns_presets" :key="key">
                    <template v-if="item.name === selected_preset">
                      <gg-text-field
                        v-for="(server, sub_key) in item.servers" :key="sub_key"
                        v-model="item.servers[sub_key]"
                        outlined
                        :label="`Nameserver ${sub_key + 1}`"
                        :value="server"
                        :disabled="!isSelectedCustomPreset"
                        :name="`Nameserver ${sub_key + 1}`"
                        v-validate="'ip_or_fqdn'"
                        :error-messages="errors.collect(`Nameserver ${sub_key + 1}`)"
                        append-icon="mdi-trash-can-outline"
                        @click:append="removeField(sub_key)"
                      ></gg-text-field>
                    </template>
                  </div>
                  <gg-btn
                    class="mr-4"
                    secondary
                    v-if="canAddNameserver"
                    @click="addField">
                    Add additional nameserver
                  </gg-btn>
                  <gg-btn
                    :loading="buttons.save_ns"
                    class="mr-2"
                    @click="saveNS">
                    Save changes
                  </gg-btn>
                </v-form>
              </v-col>
            </gg-card-text>
          </gg-card>
        </gg-loading>
      </v-col>
      <v-col v-if="isHostedSelected" cols="12">
        <name-servers-editor
          :domainid="domainid"
          :domain="domain.domain"
        />
      </v-col>
      <v-col cols="12" v-if="isCustomNSAvailable">
        <name-servers-registration />
      </v-col>
      <v-col v-else cols="12">
        <gg-card>
          <gg-card-header>
            Nameserver Registration
          </gg-card-header>
          <gg-card-text>
            <p class="body-1">
              Your domain requires special nameserver registration steps. Please contact Support for
              assistance with registering nameservers.
            </p>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import amapi from '@/api/amapi'
import { mapGetters, mapActions } from 'vuex'
import NameServersRegistration from './NameServersRegisration'
import NameServersEditor from './DnsEditor/NameServersEditor'
import { domains_without_custom_ns } from '@/helpers/helpers'

export default {
  name: 'DomainNameservers',
  components: {
    NameServersRegistration,
    NameServersEditor,
  },
  props: {
    domainid: {
      type: String,
      required: true
    }
  },
  data: () => ({
    domains_without_custom_ns,
    dns_presets: [
      {
        name: 'standard',
        printableName: 'GreenGeeks Nameservers',
        servers: [
          'ns1.greengeeks.net',
          'ns2.greengeeks.net'
        ]
      },
      {
        name: 'hosted',
        printableName: 'DNS Hosted',
        servers: [
          'dns1.name-services.com',
          'dns2.name-services.com',
          'dns3.name-services.com',
          'dns4.name-services.com',
          'dns5.name-services.com',
        ]
      },
      {
        name: 'custom',
        printableName: 'Custom',
        servers: ['', '']
      }
    ],
    selected_preset: '',
    buttons: {
      save_ns: false,
    },
  }),
  methods: {
    ...mapActions(['getDomain', 'reportError', 'reportSuccess']),
    setDefaultPreset () {
      this.selected_preset = this.domain.dnspreset
    },
    setCustomServers () {
      if (this.domain.dnspreset === 'custom') {
        this.dns_presets.forEach(item => {
          if (item.name !== 'custom') return

          item.servers = []

          this.domain.dns.forEach(dns => {
            item.servers.push(dns)
          })
        })
      }
    },
    addField () {
      this.dns_presets.forEach(item => {
        if (item.name !== this.selected_preset) return

        item.servers.push('')
      })
    },
    removeField (index) {
      this.dns_presets.forEach(item => {
        if (item.name !== this.selected_preset) return

        item.servers.splice(index, 1)
      })
    },
    async saveNS () {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      const selected_ns = this.dns_presets.filter(preset => {
        return preset.name === this.selected_preset
      }).map(item => item.servers)[0].filter(ns => !!ns)

      this.buttons.save_ns = true
      const data = {
        domain_id: this.domainid,
        nameservers: selected_ns,
      }
      try {
        const response = await amapi.makecall('domain_updatenameservers', data)
        if (response.result === 'success') {
          // refresh domain data
          this.getDomain({ domain_id: this.domainid, refresh: true })
          // this.setCustomServers()
          this.reportSuccess(response.resultmsg)
        } else {
          this.reportError(response.resultmsg)
        }
      } catch (e) {
        this.reportError(e)
      }
      this.buttons.save_ns = false
    },
  },
  computed: {
    ...mapGetters(['domain', 'domainPopulated']),
    isSelectedCustomPreset () {
      return this.selected_preset === 'custom'
    },
    isCustomNSAvailable () {
      const search = domains_without_custom_ns.find(item => item.value === this.domain.domain)

      return search === undefined
    },
    isHostedSelected () {
      return this.selected_preset === 'hosted'
    },
    canAddNameserver () {
      const selected_ns = this.dns_presets.filter(preset => {
        return preset.name === this.selected_preset
      }).map(item => item.servers)[0]
      return this.isSelectedCustomPreset && selected_ns.length < 5
    },
  },
  watch: {
    domainPopulated () {
      this.setDefaultPreset()
      this.setCustomServers()
    },
  },
  created () {
    this.setDefaultPreset()
    this.setCustomServers()
  }
}
</script>
